import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import axios from 'axios';
import {
  settingsApi,
  getPublicBranding,
  type PublicBranding,
  type LanguagesSettings,
  type UpdateLanguagesSettingsDTO,
} from '../api/settings';
import { createMutationErrorHandler } from '@/lib/mutationErrorHandler';
import { hydrateEnabledLocales } from '@/lib/i18n/localeRegistry';
import { useToast } from './useToast';
import type {
  SystemSettings,
  UpdateSettingsDTO,
  PaymentMethodsResponse,
  UpdatePaymentMethodsDTO,
  GatewaySettings,
  UpdateGatewaySettingsDTO,
  GatewayTestResponse,
} from '../types/settings';
import type {
  CompanySettings,
  CompanySettingsFormData,
  FileUploadResponse,
  CompanyFileType,
} from '../types/companySettings';
import type { BrandingSettings, UpdateBrandingSettingsDTO } from '../types/branding';
import type {
  EmailSettings,
  UpdateEmailSettingsDTO,
  EmailTestResponse,
  MailProbeResponse,
  MailSendTestResponse,
  MailSendTestPayload,
  TicketSettings,
  UpdateTicketSettingsDTO,
  ContactDefaultsSettings,
  UpdateContactDefaultsSettingsDTO,
} from '../types/emailSettings';
import type {
  DocumensoSettings,
  UpdateDocumensoSettingsDTO,
  DocumensoTestResponse,
  BillingPlatformSettings,
  BillingPlatformTestResponse,
  FacturaDirectaCredentialsSettings,
  UpdateFacturaDirectaCredentialsDTO,
  FacturaDirectaCredentialsTestResponse,
  AnthropicSettings,
  UpdateAnthropicSettingsDTO,
  AnthropicTestResponse,
  UiSettings,
  UpdateUiSettingsDTO,
  SlaComplianceSettings,
  UpdateSlaComplianceSettingsDTO,
} from '../types/settings';

// Query keys
export const settingsKeys = {
  all: ['settings'] as const,
  detail: () => [...settingsKeys.all, 'detail'] as const,
  paymentMethods: () => [...settingsKeys.all, 'payment-methods'] as const,
  gateways: () => [...settingsKeys.all, 'gateways'] as const,
  company: () => [...settingsKeys.all, 'company'] as const,
  email: () => [...settingsKeys.all, 'email'] as const,
  tickets: () => [...settingsKeys.all, 'tickets'] as const,
  contactDefaults: () => [...settingsKeys.all, 'contact-defaults'] as const,
  documenso: () => [...settingsKeys.all, 'documenso'] as const,
  billingPlatform: () => [...settingsKeys.all, 'billing-platform'] as const,
  facturaDirectaCredentials: () => [...settingsKeys.all, 'factura-directa-credentials'] as const,
  anthropic: () => [...settingsKeys.all, 'anthropic'] as const,
  ui: () => [...settingsKeys.all, 'ui'] as const,
  branding: () => [...settingsKeys.all, 'branding'] as const,
  slaTargets: () => [...settingsKeys.all, 'sla-targets'] as const,
  languages: () => [...settingsKeys.all, 'languages'] as const,
  publicBranding: () => ['public-branding'] as const,
};

/**
 * Hook to fetch system settings
 */
export function useSettings(): UseQueryResult<SystemSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.detail(),
    queryFn: () => settingsApi.getAll(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update system settings
 */
export function useUpdateSettings(): UseMutationResult<SystemSettings, Error, UpdateSettingsDTO> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateSettingsDTO) => settingsApi.update(data),
    onSuccess: (updatedSettings) => {
      // Update the cache
      queryClient.setQueryData(settingsKeys.detail(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Payment Methods Hooks
// ============================================================================

/**
 * Hook to fetch payment method settings
 */
export function usePaymentMethods(): UseQueryResult<PaymentMethodsResponse['data'], Error> {
  return useQuery({
    queryKey: settingsKeys.paymentMethods(),
    queryFn: () => settingsApi.paymentMethods.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update payment method settings
 */
export function useUpdatePaymentMethods(): UseMutationResult<
  PaymentMethodsResponse['data'],
  Error,
  UpdatePaymentMethodsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdatePaymentMethodsDTO) => settingsApi.paymentMethods.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.paymentMethods(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Gateway Settings Hooks
// ============================================================================

/**
 * Hook to fetch gateway settings
 */
export function useGatewaySettings(): UseQueryResult<GatewaySettings, Error> {
  return useQuery({
    queryKey: settingsKeys.gateways(),
    queryFn: () => settingsApi.gateways.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update gateway settings
 */
export function useUpdateGatewaySettings(): UseMutationResult<
  GatewaySettings,
  Error,
  UpdateGatewaySettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateGatewaySettingsDTO) => settingsApi.gateways.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.gateways(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to test gateway connection
 */
export function useTestGatewayConnection(): UseMutationResult<
  GatewayTestResponse,
  Error,
  {
    gateway: 'stripe' | 'mollie';
    credentials?: { secret_key?: string; publishable_key?: string; api_key?: string };
  }
> {
  // No onError here: both callers render their own inline test-result
  // (success/failure) UI — a generic toast on top would be redundant noise.
  return useMutation({
    mutationFn: ({ gateway, credentials }) =>
      settingsApi.gateways.testConnection(gateway, credentials),
  });
}

// ============================================================================
// Company Settings Hooks
// ============================================================================

/**
 * Hook to fetch company settings
 */
export function useCompanySettings(): UseQueryResult<CompanySettings, Error> {
  return useQuery({
    queryKey: settingsKeys.company(),
    queryFn: () => settingsApi.company.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update company settings
 */
export function useUpdateCompanySettings(): UseMutationResult<
  CompanySettings,
  Error,
  Partial<CompanySettingsFormData>
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: Partial<CompanySettingsFormData>) => settingsApi.company.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.company(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to upload company file (logo or favicon)
 */
export function useUploadCompanyFile(): UseMutationResult<
  FileUploadResponse,
  Error,
  { type: CompanyFileType; file: File }
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ type, file }: { type: CompanyFileType; file: File }) =>
      settingsApi.company.uploadFile(type, file),
    onSuccess: () => {
      // Invalidate company settings to refetch with new file URL
      queryClient.invalidateQueries({ queryKey: settingsKeys.company() });
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to delete company file (logo or favicon)
 */
export function useDeleteCompanyFile(): UseMutationResult<
  { message: string },
  Error,
  CompanyFileType
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (type: CompanyFileType) => settingsApi.company.deleteFile(type),
    onSuccess: () => {
      // Invalidate company settings to refetch without file URL
      queryClient.invalidateQueries({ queryKey: settingsKeys.company() });
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Email Settings Hooks
// ============================================================================

export function useEmailSettings(): UseQueryResult<EmailSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.email(),
    queryFn: () => settingsApi.email.get(),
    staleTime: 60000,
  });
}

/**
 * Update email settings.
 *
 * FU8 — accepts a `probe` flag. When true, the backend runs a connection
 * probe BEFORE persisting and returns 422 if it fails (no DB write).
 */
export function useUpdateEmailSettings(): UseMutationResult<
  EmailSettings,
  Error,
  { data: UpdateEmailSettingsDTO; probe?: boolean }
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ data, probe }) => settingsApi.email.update(data, { probe }),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.email(), updatedSettings);
    },
    // Custom (not the generic createMutationErrorHandler): when a pre-save
    // probe fails, the backend returns a generic top-level `message`
    // ("changes not saved") PLUS the specific reason nested under
    // `probe.message` (e.g. "Connection timed out", "Authentication
    // failed") — surface the specific one so admins can actually debug their
    // SMTP config, instead of a vague fallback.
    onError: (error: Error) => {
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as
          | { message?: string; probe?: { message?: string } }
          | undefined;
        const probeMessage = data?.probe?.message;
        toast({
          title: 'Error',
          description: probeMessage ?? data?.message ?? 'Something went wrong. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * FU8 — Probe the configured mail transport (HELO/EHLO/AUTH for SMTP, API
 * reachability for Mailgun/SES/Postmark). NEVER sends mail.
 */
export function useTestMailConnection(): UseMutationResult<
  MailProbeResponse,
  Error,
  void
> {
  // No onError here: the sole caller (EmailSettingsPage) renders its own
  // inline success/failure result next to the button — a toast on top would
  // be redundant.
  return useMutation({
    mutationFn: () => settingsApi.email.testConnection(),
  });
}

/**
 * FU8 — Send a real test email to the supplied recipient (defaults to the
 * acting admin). Rate-limited 5/hour/admin server-side.
 */
export function useSendMailTest(): UseMutationResult<
  MailSendTestResponse,
  Error,
  MailSendTestPayload
> {
  // No onError here: the sole caller (EmailSettingsPage) shows its own toast,
  // including rate-limit-specific copy a generic handler wouldn't know about.
  return useMutation({
    mutationFn: (payload: MailSendTestPayload) => settingsApi.email.sendTest(payload),
  });
}

/**
 * @deprecated FU8 — replaced by useTestMailConnection (probe) +
 * useSendMailTest (send). Retained as a backwards-compatible thin wrapper
 * around useSendMailTest so any in-progress callers that reference it still
 * compile. Remove once no callers remain.
 */
export function useTestEmailConnection(): UseMutationResult<
  EmailTestResponse,
  Error,
  string
> {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (recipient: string) => {
      const result = await settingsApi.email.sendTest({ recipient });
      return {
        success: result.success,
        message: result.message,
        error: result.error ?? undefined,
      };
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Ticket Settings Hooks
// ============================================================================

export function useTicketSettings(): UseQueryResult<TicketSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.tickets(),
    queryFn: () => settingsApi.tickets.get(),
    staleTime: 60000,
  });
}

export function useUpdateTicketSettings(): UseMutationResult<
  TicketSettings,
  Error,
  UpdateTicketSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateTicketSettingsDTO) => settingsApi.tickets.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.tickets(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Contact Defaults Settings Hooks
// ============================================================================

export function useContactDefaultsSettings(): UseQueryResult<ContactDefaultsSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.contactDefaults(),
    queryFn: () => settingsApi.contactDefaults.get(),
    staleTime: 60000,
  });
}

export function useUpdateContactDefaultsSettings(): UseMutationResult<
  ContactDefaultsSettings,
  Error,
  UpdateContactDefaultsSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateContactDefaultsSettingsDTO) => settingsApi.contactDefaults.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.contactDefaults(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Documenso (E-Signature) Settings Hooks
// ============================================================================

/**
 * Hook to fetch Documenso integration settings
 */
export function useDocumensoSettings(): UseQueryResult<DocumensoSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.documenso(),
    queryFn: () => settingsApi.documenso.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update Documenso integration settings
 */
export function useUpdateDocumensoSettings(): UseMutationResult<
  DocumensoSettings,
  Error,
  UpdateDocumensoSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateDocumensoSettingsDTO) => settingsApi.documenso.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.documenso(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to test Documenso connection
 */
export function useTestDocumensoConnection(): UseMutationResult<
  DocumensoTestResponse,
  Error,
  { base_url?: string; api_key?: string } | void
> {
  // No onError here: both callers render an inline success/failure result
  // (DocumensoSettingsPage directly, the onboarding step via the shared
  // `TestConnectionButton`, which is explicitly toast-free by design).
  return useMutation({
    mutationFn: (payload) => settingsApi.documenso.testConnection(payload ?? undefined),
  });
}

// ============================================================================
// Billing Platform Settings Hooks
// ============================================================================

/**
 * Hook to fetch billing platform settings and available drivers
 */
export function useBillingPlatformSettings(): UseQueryResult<BillingPlatformSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.billingPlatform(),
    queryFn: () => settingsApi.billingPlatform.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update the active billing platform
 */
export interface UpdateBillingPlatformPayload {
  platform: string;
  reason: string;
  force?: boolean;
}

export function useUpdateBillingPlatform(): UseMutationResult<
  {
    message: string;
    connection_test?: BillingPlatformTestResponse;
    unsynced_count?: number;
    requires_force?: boolean;
  },
  Error,
  UpdateBillingPlatformPayload
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ platform, reason, force }: UpdateBillingPlatformPayload) =>
      settingsApi.billingPlatform.update(platform, reason, force ?? false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.billingPlatform() });
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to test the active billing platform connection
 */
export function useTestBillingPlatformConnection(): UseMutationResult<
  BillingPlatformTestResponse,
  Error,
  string | undefined
> {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (platform?: string) => settingsApi.billingPlatform.testConnection(platform),
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Factura Directa Credentials Settings Hooks
// ============================================================================

/**
 * Hook to fetch Factura Directa credentials settings
 */
export function useFacturaDirectaCredentialsSettings(): UseQueryResult<FacturaDirectaCredentialsSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.facturaDirectaCredentials(),
    queryFn: () => settingsApi.facturaDirectaCredentials.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update Factura Directa credentials settings.
 * Invalidates BOTH facturaDirectaCredentials AND billingPlatform query keys
 * so that dependent UI (active-platform badge, onboarding wizard) refreshes.
 */
export function useUpdateFacturaDirectaCredentialsSettings(): UseMutationResult<
  FacturaDirectaCredentialsSettings,
  Error,
  UpdateFacturaDirectaCredentialsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateFacturaDirectaCredentialsDTO) =>
      settingsApi.facturaDirectaCredentials.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.facturaDirectaCredentials(), updatedSettings);
      queryClient.invalidateQueries({ queryKey: settingsKeys.billingPlatform() });
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to test Factura Directa connection
 */
export function useTestFacturaDirectaCredentialsConnection(): UseMutationResult<
  FacturaDirectaCredentialsTestResponse,
  Error,
  { api_key?: string; company_id?: string } | void
> {
  // No onError toast here: the sole caller (BillingPlatformSettingsPage)
  // renders a clear inline success/failure result for this "test connection"
  // action — a generic toast on top of that would just be noise.
  return useMutation({
    mutationFn: (payload) =>
      settingsApi.facturaDirectaCredentials.testConnection(payload ?? undefined),
  });
}

// ============================================================================
// Anthropic AI Provider Settings Hooks
// ============================================================================

/**
 * Hook to fetch Anthropic AI provider settings
 */
export function useAnthropicSettings(): UseQueryResult<AnthropicSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.anthropic(),
    queryFn: () => settingsApi.anthropic.get(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to update Anthropic AI provider settings
 */
export function useUpdateAnthropicSettings(): UseMutationResult<
  AnthropicSettings,
  Error,
  UpdateAnthropicSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateAnthropicSettingsDTO) => settingsApi.anthropic.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.anthropic(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

/**
 * Hook to test Anthropic connection
 */
export function useTestAnthropicConnection(): UseMutationResult<
  AnthropicTestResponse,
  Error,
  { api_key?: string; model?: string } | void
> {
  // No onError here: both callers render an inline success/failure result
  // (AnthropicSettingsPage directly, the onboarding step via the shared
  // toast-free `TestConnectionButton`).
  return useMutation({
    mutationFn: (payload) => settingsApi.anthropic.testConnection(payload ?? undefined),
  });
}

// ============================================================================
// Public Branding Hook (No Authentication Required)
// ============================================================================

// ============================================================================
// SLA Compliance Target Settings (TODO-6-002)
// ============================================================================

/**
 * Hook to fetch SLA compliance target settings.
 */
export function useSlaTargetSettings(): UseQueryResult<SlaComplianceSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.slaTargets(),
    queryFn: () => settingsApi.slaTargets.get(),
    staleTime: 60000,
  });
}

/**
 * Hook to update SLA compliance target settings.
 */
export function useUpdateSlaTargetSettings(): UseMutationResult<
  SlaComplianceSettings,
  Error,
  UpdateSlaComplianceSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateSlaComplianceSettingsDTO) => settingsApi.slaTargets.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.slaTargets(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// UI Settings (TODO-6-006 — legacy visibility toggle)
// ============================================================================

/**
 * Hook to fetch UI behaviour settings.
 */
export function useUiSettings(): UseQueryResult<UiSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.ui(),
    queryFn: () => settingsApi.ui.get(),
    staleTime: 60000,
  });
}

/**
 * Hook to update UI behaviour settings.
 */
export function useUpdateUiSettings(): UseMutationResult<
  UiSettings,
  Error,
  UpdateUiSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateUiSettingsDTO) => settingsApi.ui.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.ui(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Customer-platform Branding Settings (Wave 2b — social media links)
// ============================================================================

/**
 * Hook to fetch customer-platform branding settings (colors, logo,
 * hours/offices, social links). Admin-only (`settings.view`).
 */
export function useBrandingSettings(): UseQueryResult<BrandingSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.branding(),
    queryFn: () => settingsApi.branding.get(),
    staleTime: 60000,
  });
}

/**
 * Hook to update customer-platform branding settings. Partial — only keys
 * present in the payload are persisted (see `UpdateBrandingSettingsRequest`).
 */
export function useUpdateBrandingSettings(): UseMutationResult<
  BrandingSettings,
  Error,
  UpdateBrandingSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateBrandingSettingsDTO) => settingsApi.branding.update(data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(settingsKeys.branding(), updatedSettings);
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================
// Languages Settings (P3 F9 — multi-language)
// ============================================================================

/**
 * Hook to fetch Languages settings (enabled locales, default, fallback chain).
 *
 * On success it hydrates the runtime locale registry so the language switcher
 * and locale-aware formatters reflect the operator's enabled set.
 */
export function useLanguagesSettings(): UseQueryResult<LanguagesSettings, Error> {
  return useQuery({
    queryKey: settingsKeys.languages(),
    queryFn: async () => {
      const data = await settingsApi.languages.get();
      hydrateEnabledLocales({ enabled: data.enabled_locales, default: data.default_locale });
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — rarely changes
  });
}

/**
 * Hook to update Languages settings. Re-hydrates the locale registry on success.
 */
export function useUpdateLanguagesSettings(): UseMutationResult<
  LanguagesSettings,
  Error,
  UpdateLanguagesSettingsDTO
> {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: UpdateLanguagesSettingsDTO) => settingsApi.languages.update(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(settingsKeys.languages(), updated);
      hydrateEnabledLocales({ enabled: updated.enabled_locales, default: updated.default_locale });
    },
    onError: createMutationErrorHandler(toast),
  });
}

// ============================================================================

/**
 * Hook to fetch public branding settings (for login page, etc.)
 * This endpoint does NOT require authentication.
 */
export function usePublicBranding(): UseQueryResult<PublicBranding, Error> {
  return useQuery({
    queryKey: settingsKeys.publicBranding(),
    queryFn: () => getPublicBranding(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
